/**
 * Układ kolumn tabeli osadzonej w formatce rekordu — kontrakt na to, co siedzi
 * w wolnym JSON-ie `element.settings` elementu layoutu formularza.
 *
 * Tabela osadzona = tabela renderowana wewnątrz formatki kartoteki/dokumentu
 * (pierwsza: tabele dodatkowe; dalej: pozycje dokumentu, rejestr VAT, widoki SQL,
 * podlisty kontaktów/adresów/rachunków). Każde UMIESZCZENIE tabeli na formatce ma
 * własny układ kolumn: widoczność, szerokość (px), własna etykieta, wyrównanie,
 * liczba miejsc po przecinku (tylko prezentacja, tylko kolumny dziesiętne).
 *
 * Klucze w `element.settings`:
 *   - `tableColumns` — układ bazowy; pozycja w tablicy = kolejność kolumn.
 *   - `hiddenColumnIds` — ZAWSZE dodatkowa maska ukrywająca nakładana na układ
 *     bazowy. To także klucz nakładki per-tenant na układach zarządzanych przez
 *     system (tenant może tam tylko ukrywać) — nakładka jest scalana płytko do
 *     tego samego obiektu `settings`.
 *   - klucze starszych formatów (inne typy elementów, kolejne etapy) — czytane
 *     wyłącznie przez opcjonalny czytnik `legacy`, gdy `tableColumns` brak.
 *
 * UWAGA — nakładka tenanta (`hiddenColumnIds` w `tab_overrides.elementOverrides`) nie jest
 * kluczowana id elementu: tabele dodatkowe mają klucz `additional_table:<tableSlug>`, pozostałe
 * elementy — swój TYP, więc tylko elementy kluczowane typem muszą być na jednym layoucie
 * pojedyncze (dwa elementy tego samego typu dzieliłyby jedną maskę).
 *
 * Moduł jest czysty (zero React/DOM) — używa go zarówno edytor w kreatorze
 * layoutu, jak i renderery stron rekordów. Brak zapisanego układu i maski =
 * naturalna kolejność deskryptorów, wszystko widoczne, zero nadpisań (renderer
 * zachowuje się wtedy dokładnie jak przed wprowadzeniem układu).
 *
 * Konsumenci (etap 1; API kontraktu nie używa — `settings` to wolny JSON):
 *   - apps/admin: edytor kolumn w kreatorze layoutu formatki,
 *   - apps/admin: renderery tabel osadzonych na stronach rekordów.
 */

import { z } from 'zod';

// ── Stałe ────────────────────────────────────────────────────────────────────

/** Minimalna szerokość kolumny w px. */
export const EMBEDDED_TABLE_COLUMN_WIDTH_MIN = 50;
/** Maksymalna szerokość kolumny w px. */
export const EMBEDDED_TABLE_COLUMN_WIDTH_MAX = 1200;
/** Maksymalna długość własnej etykiety kolumny (po przycięciu spacji). */
export const EMBEDDED_TABLE_COLUMN_LABEL_MAX = 100;
/** Maksymalna liczba miejsc po przecinku dla kolumn dziesiętnych. */
export const EMBEDDED_TABLE_COLUMN_DECIMALS_MAX = 6;

/**
 * Typy elementów układu dopuszczalne najwyżej RAZ w całym układzie (pozycje dokumentu, rejestr VAT).
 * Nakładka per-tenant na układzie systemowym jest kluczowana TYPEM elementu, więc dwa bloki tego
 * samego typu współdzieliłyby jedną maskę ukrytych kolumn. Jedno źródło prawdy: kreator (flaga
 * `layoutSingleton` w rejestrze elementów), walidacja tras zapisu układu i przenoszenie ustawień
 * kolumn przy re-seedzie czytają tę listę.
 */
export const LAYOUT_SINGLETON_ELEMENT_TYPES = ['document_items_view', 'vat_register_view'] as const;

export type LayoutSingletonElementType = (typeof LAYOUT_SINGLETON_ELEMENT_TYPES)[number];

/** Klucz układu bazowego w `element.settings`. */
const TABLE_COLUMNS_KEY = 'tableColumns';
/** Klucz maski ukrywającej (i nakładki tenanta) w `element.settings`. */
const HIDDEN_COLUMN_IDS_KEY = 'hiddenColumnIds';

// ── Typy ─────────────────────────────────────────────────────────────────────

/** Wyrównanie zawartości kolumny. */
export type EmbeddedTableColumnAlign = 'left' | 'center' | 'right';

/**
 * Rodzaj danych kolumny — steruje domyślnym wyrównaniem (liczby do prawej)
 * i tym, czy kolumna przyjmuje `decimals` (tylko `decimal`).
 */
export type EmbeddedTableColumnKind = 'text' | 'decimal' | 'integer' | 'date' | 'boolean' | 'other';

const embeddedTableColumnAlignSchema = z.enum(['left', 'center', 'right']);

/**
 * Zapisane ustawienia pojedynczej kolumny tabeli osadzonej (jeden wpis tablicy
 * `settings.tableColumns`). Pola opcjonalne: brak = zachowanie domyślne
 * renderera (szerokość auto, etykieta z deskryptora, wyrównanie wg rodzaju,
 * formatowanie liczby bez zmian).
 */
export const embeddedTableColumnSchema = z.object({
  /** Identyfikator kolumny — odpowiada `EmbeddedTableColumnDescriptor.id`. */
  id: z.string().min(1).max(200),
  /** Czy kolumna widoczna w układzie bazowym. */
  visible: z.boolean(),
  /** Szerokość w px (brak = auto). */
  width: z
    .number()
    .int()
    .min(EMBEDDED_TABLE_COLUMN_WIDTH_MIN)
    .max(EMBEDDED_TABLE_COLUMN_WIDTH_MAX)
    .optional(),
  /** Własna etykieta nagłówka (przycinana; pusta = etykieta z deskryptora). */
  label: z.string().trim().max(EMBEDDED_TABLE_COLUMN_LABEL_MAX).optional(),
  /**
   * Wyrównanie. Pierwszeństwo: jawne `align` → `defaultAlign` deskryptora →
   * reguła wg rodzaju (do prawej dla liczb, do lewej dla reszty); brak = ostatnie dwa.
   */
  align: embeddedTableColumnAlignSchema.optional(),
  /** Miejsca po przecinku — tylko prezentacja, honorowane tylko dla `kind: 'decimal'`. */
  decimals: z.number().int().min(0).max(EMBEDDED_TABLE_COLUMN_DECIMALS_MAX).optional(),
});

export type EmbeddedTableColumn = z.infer<typeof embeddedTableColumnSchema>;

/**
 * Opis kolumny dostępnej w danej tabeli — dostarcza go konsument (definicja
 * tabeli dodatkowej, pozycji dokumentu itp.), nie zapis w `settings`.
 */
export interface EmbeddedTableColumnDescriptor {
  /** Stabilny identyfikator kolumny (klucz w `tableColumns` / `hiddenColumnIds`). */
  id: string;
  /** Domyślna etykieta nagłówka (już przetłumaczona przez konsumenta). */
  label: string;
  /** Rodzaj danych kolumny. */
  kind: EmbeddedTableColumnKind;
  /** Kolumna wymagana — nie da się jej ukryć. */
  required?: boolean;
  /** Kolumna zablokowana przez system — nie da się jej ukryć. */
  locked?: boolean;
  /**
   * Wyrównanie, gdy układ go nie ustawia — pozwala tabeli zachować dotychczasowy wygląd
   * (np. tabela dodatkowa wyrównuje wszystko do lewej). Brak = reguła wg `kind`.
   */
  defaultAlign?: EmbeddedTableColumnAlign;
  /**
   * Krótka adnotacja przy kolumnie, widoczna WYŁĄCZNIE w edytorze układu (znaczek w panelu „Kolumny",
   * podpowiedź na nagłówku) — już przetłumaczona przez konsumenta, np. „tylko w edycji" dla kolumn,
   * których widok odczytu nie rysuje. Nie wpływa na rozwiązywanie układu ani na zapis.
   */
  editorNote?: string;
}

/**
 * Kolumna po rozwiązaniu układu: deskryptor + zapis bazowy + maska. Kształt
 * roboczy edytora i rendererów — do zapisu wraca przez `buildTableColumnsPatch`.
 */
export interface ResolvedEmbeddedTableColumn {
  descriptor: EmbeddedTableColumnDescriptor;
  id: string;
  /** Widoczność z `tableColumns` / czytnika legacy / domyślna (`true`). */
  baseVisible: boolean;
  /** Id obecne w `settings.hiddenColumnIds` (nigdy dla kolumn wymaganych/zablokowanych). */
  overlayHidden: boolean;
  /** Efektywna widoczność: `(baseVisible && !overlayHidden) || forced`. */
  visible: boolean;
  /** Kolumna wymagana/zablokowana, która bez wymuszenia byłaby ukryta. */
  forced: boolean;
  /** Kolumna spoza zapisanego układu, dopisana na końcu (tylko gdy zapis istnieje). */
  isNew: boolean;
  width?: number;
  label?: string;
  align?: EmbeddedTableColumnAlign;
  decimals?: number;
}

/**
 * Czytnik starszego formatu układu dla danego typu elementu. Wołany WYŁĄCZNIE,
 * gdy `settings.tableColumns` brak albo jest nieprawidłowe/puste. Zwraca
 * `undefined`, gdy starszego zapisu też nie ma.
 */
export type EmbeddedTableLegacyReader = (
  settings: Record<string, unknown>,
  descriptors: readonly EmbeddedTableColumnDescriptor[],
) => EmbeddedTableColumn[] | undefined;

/** Częściowa zmiana nadpisań kolumny: `undefined` = bez zmian, `null` = usuń nadpisanie. */
export interface EmbeddedTableColumnPatch {
  width?: number | null;
  label?: string | null;
  align?: EmbeddedTableColumnAlign | null;
  decimals?: number | null;
}

/**
 * Tryb zapisu układu:
 *   - `'full'` — układ własny: zapisujemy całe `tableColumns`, maska jest wtapiana.
 *   - `'hideOnly'` — układ systemowy: tenant zapisuje tylko maskę `hiddenColumnIds`.
 */
export type EmbeddedTableColumnsWriteMode = 'full' | 'hideOnly';

// ── Pomocnicze ───────────────────────────────────────────────────────────────

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Kolumna wymagana lub zablokowana („przypięta") — nie da się jej ukryć.
 * Jedno źródło reguły dla kontraktu i dla edytora (panel „Kolumny", menu nagłówka).
 */
export function isEmbeddedTableColumnPinned(descriptor: EmbeddedTableColumnDescriptor): boolean {
  return descriptor.required === true || descriptor.locked === true;
}

function isAlign(value: unknown): value is EmbeddedTableColumnAlign {
  return embeddedTableColumnAlignSchema.safeParse(value).success;
}

function clampInt(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Math.round(value)));
}

/**
 * Dzieli tekst na jednostki niepodzielne przy cięciu: klastry grafemów (flaga, emoji ze
 * zlepkiem ZWJ, litera z akcentem w postaci rozłożonej) gdy środowisko ma `Intl.Segmenter`,
 * inaczej punkty kodowe (nie rozetnie pary zastępczej, ale może rozciąć flagę lub zlepek ZWJ).
 */
function splitIntoUnits(text: string): string[] {
  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(segmenter.segment(text), (part) => part.segment);
  }
  return Array.from(text);
}

/**
 * Przycina etykietę i ucina do limitu; pusta po przycięciu = brak etykiety.
 *
 * Ucina po grafemach (nie w połowie flagi, zlepka emoji ani pary zastępczej; bez
 * `Intl.Segmenter` — po punktach kodowych), a limit liczy w jednostkach UTF-16 — tak samo
 * jak `.max()` schematu zod, więc wynik zawsze przechodzi `embeddedTableColumnSchema`.
 * Gdy już PIERWSZY grafem jest dłuższy niż limit (np. litera z setką znaków łączących), nie
 * mieści się sam i cięcie po grafemach dałoby pusty wynik — wtedy tniemy po punktach kodowych,
 * żeby nie zgubić całej niepustej etykiety (nadal bez rozcinania pary zastępczej).
 * Jedno źródło reguły dla kontraktu i pola „Podpis" w edytorze.
 */
export function normalizeEmbeddedTableColumnLabel(value: string): string | undefined {
  const trimmed = value.trim();
  let out = '';
  if (trimmed.length <= EMBEDDED_TABLE_COLUMN_LABEL_MAX) {
    out = trimmed;
  } else {
    for (const unit of splitIntoUnits(trimmed)) {
      if (out.length + unit.length > EMBEDDED_TABLE_COLUMN_LABEL_MAX) break;
      out += unit;
    }
    if (out === '') {
      // `for...of` po napisie iteruje punktami kodowymi.
      for (const codePoint of trimmed) {
        if (out.length + codePoint.length > EMBEDDED_TABLE_COLUMN_LABEL_MAX) break;
        out += codePoint;
      }
    }
  }
  const clipped = out.trim();
  return clipped.length > 0 ? clipped : undefined;
}

/** Szerokość do zapisu: liczba skończona → zaokrąglona i docięta do limitów. */
function normalizeWidth(value: number): number | undefined {
  if (!Number.isFinite(value)) return undefined;
  return clampInt(value, EMBEDDED_TABLE_COLUMN_WIDTH_MIN, EMBEDDED_TABLE_COLUMN_WIDTH_MAX);
}

/** Miejsca po przecinku do zapisu: liczba skończona → zaokrąglona i docięta do 0..max. */
function normalizeDecimals(value: number): number | undefined {
  if (!Number.isFinite(value)) return undefined;
  return clampInt(value, 0, EMBEDDED_TABLE_COLUMN_DECIMALS_MAX);
}

/**
 * Pola widoczności kolumny wyliczane z jednego miejsca (rozwiązanie układu
 * i operacje edytora liczą je tak samo).
 */
function computeVisibility(
  descriptor: EmbeddedTableColumnDescriptor,
  baseVisible: boolean,
  maskHit: boolean,
): Pick<ResolvedEmbeddedTableColumn, 'baseVisible' | 'overlayHidden' | 'visible' | 'forced'> {
  const pinned = isEmbeddedTableColumnPinned(descriptor);
  const overlayHidden = !pinned && maskHit;
  const forced = pinned && (!baseVisible || maskHit);
  const visible = (baseVisible && !overlayHidden) || forced;
  return { baseVisible, overlayHidden, visible, forced };
}

/** Nadpisania z zapisu, z regułami: etykieta niepusta, `decimals` tylko dla `decimal`. */
function overridesFor(
  descriptor: EmbeddedTableColumnDescriptor,
  entry: EmbeddedTableColumn | undefined,
): Pick<ResolvedEmbeddedTableColumn, 'width' | 'label' | 'align' | 'decimals'> {
  const out: Pick<ResolvedEmbeddedTableColumn, 'width' | 'label' | 'align' | 'decimals'> = {};
  if (!entry) return out;
  if (entry.width !== undefined) out.width = entry.width;
  if (entry.label !== undefined) {
    const label = normalizeEmbeddedTableColumnLabel(entry.label);
    if (label !== undefined) out.label = label;
  }
  if (entry.align !== undefined) out.align = entry.align;
  if (entry.decimals !== undefined && descriptor.kind === 'decimal') out.decimals = entry.decimals;
  return out;
}

// ── Odczyt ───────────────────────────────────────────────────────────────────

/**
 * Tolerancyjny odczyt `settings.tableColumns`.
 *
 * - Nie-tablica albo tablica bez żadnego poprawnego wpisu → `undefined`
 *   (= brak zapisanego układu).
 * - Wpis bez poprawnego `id` lub `visible` jest POMIJANY w całości.
 * - Nieprawidłowe pole opcjonalne (np. szerokość spoza limitów, `decimals` > max,
 *   za długa etykieta, nieznane wyrównanie) jest ODRZUCANE samo — reszta wpisu
 *   zostaje. Świadomie nie docinamy wartości przy odczycie (docinanie robi tylko
 *   strona zapisu: `patchEmbeddedTableColumn` / `buildTableColumnsPatch`), żeby
 *   uszkodzony zapis nie udawał świadomego wyboru usera.
 * - Nieznane klucze wpisu są gubione. Duplikaty id NIE są tu usuwane —
 *   deduplikację (pierwsze wystąpienie wygrywa) robi `resolveEmbeddedTableColumns`.
 */
export function parseEmbeddedTableColumns(raw: unknown): EmbeddedTableColumn[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const shape = embeddedTableColumnSchema.shape;
  const out: EmbeddedTableColumn[] = [];
  for (const item of raw) {
    if (!isPlainRecord(item)) continue;
    const id = shape.id.safeParse(item.id);
    const visible = shape.visible.safeParse(item.visible);
    if (!id.success || !visible.success) continue;
    const entry: EmbeddedTableColumn = { id: id.data, visible: visible.data };
    if (item.width !== undefined) {
      const width = shape.width.safeParse(item.width);
      if (width.success && width.data !== undefined) entry.width = width.data;
    }
    if (item.label !== undefined) {
      const label = shape.label.safeParse(item.label);
      if (label.success && label.data !== undefined) entry.label = label.data;
    }
    if (item.align !== undefined) {
      const align = shape.align.safeParse(item.align);
      if (align.success && align.data !== undefined) entry.align = align.data;
    }
    if (item.decimals !== undefined) {
      const decimals = shape.decimals.safeParse(item.decimals);
      if (decimals.success && decimals.data !== undefined) entry.decimals = decimals.data;
    }
    out.push(entry);
  }
  return out.length > 0 ? out : undefined;
}

/**
 * Tolerancyjny odczyt maski `settings.hiddenColumnIds`: wpisy inne niż niepusty string są
 * pomijane, duplikaty usuwane (pierwsze wystąpienie wygrywa). Nie-tablica albo brak
 * poprawnych wpisów → `undefined` (= brak maski).
 */
export function parseEmbeddedTableHiddenColumnIds(raw: unknown): string[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const ids = new Set<string>();
  for (const item of raw) {
    if (typeof item === 'string' && item.length > 0) ids.add(item);
  }
  return ids.size > 0 ? [...ids] : undefined;
}

/**
 * Rozwiązuje układ kolumn dla danego umieszczenia tabeli.
 *
 * Kolejność źródeł układu bazowego: `settings.tableColumns` → (gdy brak lub
 * nieprawidłowe/puste) `opts.legacy` → brak zapisu (naturalna kolejność
 * deskryptorów, wszystko widoczne). Na wynik ZAWSZE nakładana jest maska
 * `settings.hiddenColumnIds`.
 *
 * Reguły:
 *   - wpisy zapisu bez deskryptora są pomijane; duplikaty id — wygrywa pierwszy,
 *   - deskryptory spoza zapisu trafiają na koniec jako widoczne, z `isNew: true`
 *     (tylko gdy zapis istnieje — bez zapisu nic nie jest „nowe"),
 *   - kolumny wymagane/zablokowane zapisane lub zamaskowane jako ukryte są
 *     wymuszenie pokazywane (`forced: true`),
 *   - `decimals` tylko dla `kind: 'decimal'`; pusta etykieta = brak etykiety.
 */
export function resolveEmbeddedTableColumns(
  descriptors: readonly EmbeddedTableColumnDescriptor[],
  settings: Record<string, unknown> | undefined | null,
  opts?: { legacy?: EmbeddedTableLegacyReader },
): ResolvedEmbeddedTableColumn[] {
  const source: Record<string, unknown> = isPlainRecord(settings) ? settings : {};

  let saved = parseEmbeddedTableColumns(source[TABLE_COLUMNS_KEY]);
  if (saved === undefined && opts?.legacy) {
    // Wynik czytnika przepuszczamy przez ten sam tolerancyjny odczyt (te same reguły pól).
    saved = parseEmbeddedTableColumns(opts.legacy(source, descriptors));
  }
  const hasSaved = saved !== undefined;
  const mask = new Set(parseEmbeddedTableHiddenColumnIds(source[HIDDEN_COLUMN_IDS_KEY]));

  const byId = new Map<string, EmbeddedTableColumnDescriptor>();
  for (const descriptor of descriptors) {
    if (!byId.has(descriptor.id)) byId.set(descriptor.id, descriptor);
  }

  const seen = new Set<string>();
  const out: ResolvedEmbeddedTableColumn[] = [];

  for (const entry of saved ?? []) {
    const descriptor = byId.get(entry.id);
    if (!descriptor || seen.has(entry.id)) continue;
    seen.add(entry.id);
    out.push({
      descriptor,
      id: descriptor.id,
      ...computeVisibility(descriptor, entry.visible, mask.has(descriptor.id)),
      isNew: false,
      ...overridesFor(descriptor, entry),
    });
  }

  for (const descriptor of descriptors) {
    if (seen.has(descriptor.id)) continue;
    seen.add(descriptor.id);
    out.push({
      descriptor,
      id: descriptor.id,
      ...computeVisibility(descriptor, true, mask.has(descriptor.id)),
      isNew: hasSaved,
    });
  }

  return out;
}

/**
 * Efektywne wyrównanie kolumny: jawne ustawienie z układu, inaczej `defaultAlign`
 * deskryptora, inaczej do prawej dla liczb (`decimal`/`integer`), do lewej dla reszty.
 */
export function effectiveColumnAlign(
  col: Pick<ResolvedEmbeddedTableColumn, 'align' | 'descriptor'>,
): EmbeddedTableColumnAlign {
  if (col.align !== undefined) return col.align;
  if (col.descriptor.defaultAlign !== undefined) return col.descriptor.defaultAlign;
  return col.descriptor.kind === 'decimal' || col.descriptor.kind === 'integer' ? 'right' : 'left';
}

// ── Zapis ────────────────────────────────────────────────────────────────────

/**
 * Buduje płytką łatkę do scalenia z `element.settings`.
 *
 * - `'full'` (układ własny): `{ tableColumns, hiddenColumnIds: undefined, ...legacyKeys: undefined }`.
 *   Maska jest wtapiana w `visible`, kolumny wymagane/zablokowane zapisywane
 *   jako `visible: true`, puste pola opcjonalne pomijane, wartości docinane do
 *   limitów schematu. `undefined` przy kluczach maski i legacy znika przy
 *   serializacji JSON — po scaleniu klucze te przestają istnieć.
 * - `'hideOnly'` (układ systemowy): wyłącznie `{ hiddenColumnIds }` — id kolumn
 *   ukrytych nakładką, nigdy wymaganych/zablokowanych.
 */
export function buildTableColumnsPatch(
  resolved: readonly ResolvedEmbeddedTableColumn[],
  mode: EmbeddedTableColumnsWriteMode,
  opts?: { legacyKeys?: readonly string[] },
): Record<string, unknown> {
  if (mode === 'hideOnly') {
    const hiddenColumnIds = resolved
      .filter((col) => col.overlayHidden && !isEmbeddedTableColumnPinned(col.descriptor))
      .map((col) => col.id);
    return { [HIDDEN_COLUMN_IDS_KEY]: hiddenColumnIds };
  }

  const tableColumns: EmbeddedTableColumn[] = resolved.map((col) => {
    const entry: EmbeddedTableColumn = {
      id: col.id,
      visible:
        isEmbeddedTableColumnPinned(col.descriptor) || (col.baseVisible && !col.overlayHidden),
    };
    if (col.width !== undefined) {
      const width = normalizeWidth(col.width);
      if (width !== undefined) entry.width = width;
    }
    if (col.label !== undefined) {
      const label = normalizeEmbeddedTableColumnLabel(col.label);
      if (label !== undefined) entry.label = label;
    }
    if (col.align !== undefined && isAlign(col.align)) entry.align = col.align;
    if (col.decimals !== undefined && col.descriptor.kind === 'decimal') {
      const decimals = normalizeDecimals(col.decimals);
      if (decimals !== undefined) entry.decimals = decimals;
    }
    return entry;
  });

  const patch: Record<string, unknown> = {
    [TABLE_COLUMNS_KEY]: tableColumns,
    [HIDDEN_COLUMN_IDS_KEY]: undefined,
  };
  for (const key of opts?.legacyKeys ?? []) {
    if (key === TABLE_COLUMNS_KEY || key === HIDDEN_COLUMN_IDS_KEY) continue;
    patch[key] = undefined;
  }
  return patch;
}

// ── Operacje edytora (czyste: zawsze nowa tablica, bez mutacji) ──────────────

/**
 * Przenosi kolumnę `activeId` na pozycję kolumny `overId` (semantyka `arrayMove`
 * z dnd-kit). Nieznane id albo to samo id = kopia bez zmian.
 */
export function moveEmbeddedTableColumn(
  resolved: readonly ResolvedEmbeddedTableColumn[],
  activeId: string,
  overId: string,
): ResolvedEmbeddedTableColumn[] {
  const next = resolved.slice();
  const from = next.findIndex((col) => col.id === activeId);
  const to = next.findIndex((col) => col.id === overId);
  if (from < 0 || to < 0 || from === to) return next;
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return resolved.slice();
  next.splice(to, 0, moved);
  return next;
}

/**
 * Id jedynej widocznej kolumny albo `null`, gdy widocznych jest więcej (albo żadna). Ostatniej
 * widocznej kolumny nie da się ukryć: tabela bez żadnej kolumny nie pokazałaby wierszy, więc nie
 * dałoby się ich zobaczyć ani usunąć (decyzja z 2026-10-21, wspólna dla wszystkich tabel). Liczy
 * się widoczność efektywna (`visible`), więc w trybie `hideOnly` także kolumny ukryte w układzie
 * bazowym. Kolumna przypięta (wymagana/zablokowana) jest zawsze widoczna, więc przy niej żadna
 * inna kolumna nie bywa ostatnią.
 */
export function lastVisibleEmbeddedTableColumnId(
  resolved: readonly ResolvedEmbeddedTableColumn[],
): string | null {
  let lastId: string | null = null;
  for (const col of resolved) {
    if (!col.visible) continue;
    if (lastId !== null) return null;
    lastId = col.id;
  }
  return lastId;
}

/**
 * Zmienia widoczność kolumny.
 *
 * - `'full'`: zmienia `baseVisible` i czyści `overlayHidden` (maska i tak jest
 *   wtapiana przy zapisie).
 * - `'hideOnly'`: zmienia wyłącznie `overlayHidden`. Kolumny ukrytej w układzie
 *   bazowym (`baseVisible: false`) tenant nie może pokazać — próba pokazania to
 *   no-op (flaga nakładki zostaje bez zmian).
 * - Kolumna wymagana/zablokowana, ukrycie OSTATNIEJ widocznej kolumny
 *   (`lastVisibleEmbeddedTableColumnId`) albo nieznane id: no-op.
 */
export function setEmbeddedTableColumnVisible(
  resolved: readonly ResolvedEmbeddedTableColumn[],
  id: string,
  visible: boolean,
  mode: EmbeddedTableColumnsWriteMode,
): ResolvedEmbeddedTableColumn[] {
  if (!visible && lastVisibleEmbeddedTableColumnId(resolved) === id) return [...resolved];
  return resolved.map((col) => {
    if (col.id !== id || isEmbeddedTableColumnPinned(col.descriptor)) return col;
    if (mode === 'full') {
      return { ...col, ...computeVisibility(col.descriptor, visible, false) };
    }
    if (visible && !col.baseVisible) return col;
    return { ...col, ...computeVisibility(col.descriptor, col.baseVisible, !visible) };
  });
}

/**
 * Zmienia nadpisania prezentacji kolumny. `undefined` w łatce = bez zmian,
 * `null` = usuń nadpisanie.
 *
 * - szerokość: zaokrąglana i docinana do `WIDTH_MIN..WIDTH_MAX`; nieskończona/NaN = usunięcie,
 * - etykieta: przycinana, ucinana do `LABEL_MAX`; pusta = usunięcie,
 * - wyrównanie: tylko `left`/`center`/`right` (inna wartość = bez zmian),
 * - `decimals`: zaokrąglane i docinane do `0..DECIMALS_MAX`, tylko dla `kind: 'decimal'`
 *   (dla innych rodzajów liczba jest ignorowana, `null` usuwa).
 *
 * Widoczność kolumny nie jest ruszana. Nieznane id = kopia bez zmian.
 */
export function patchEmbeddedTableColumn(
  resolved: readonly ResolvedEmbeddedTableColumn[],
  id: string,
  patch: EmbeddedTableColumnPatch,
): ResolvedEmbeddedTableColumn[] {
  return resolved.map((col) => {
    if (col.id !== id) return col;
    const next: ResolvedEmbeddedTableColumn = { ...col };

    if (patch.width === null) {
      delete next.width;
    } else if (patch.width !== undefined) {
      const width = normalizeWidth(patch.width);
      if (width === undefined) delete next.width;
      else next.width = width;
    }

    if (patch.label === null) {
      delete next.label;
    } else if (patch.label !== undefined) {
      const label = normalizeEmbeddedTableColumnLabel(patch.label);
      if (label === undefined) delete next.label;
      else next.label = label;
    }

    if (patch.align === null) {
      delete next.align;
    } else if (patch.align !== undefined && isAlign(patch.align)) {
      next.align = patch.align;
    }

    if (patch.decimals === null) {
      delete next.decimals;
    } else if (patch.decimals !== undefined && col.descriptor.kind === 'decimal') {
      const decimals = normalizeDecimals(patch.decimals);
      if (decimals === undefined) delete next.decimals;
      else next.decimals = decimals;
    }

    return next;
  });
}
